import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, CheckCircle, PlusCircle } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { DateTimeText, Id, Mono } from '../../components/ui/Mono';
import Select from '../../components/ui/Select';
import { SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import ReturnTriageModal from '../../components/warehouse/returns/ReturnTriageModal';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import { ExtractPaging } from '../../utils/aggregation';
import { FormatInt } from '../../utils/format';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const BLANK_CELL = <span className="text-mono-400">—</span>;

/**
 * /warehouse/returns — ENTRADA DE DEVOLUÇÕES (WMS).
 *
 * Registrar a CHEGADA (cabeçalho, sem saldo — a caixa ainda não foi aberta) e
 * SEPARAR item a item: volta ao estoque vendável (posição de devolução-boa,
 * sector DEVOLUCAO) × vai ao fornecedor (RMA). O saldo nasce na separação,
 * como LPN origin_type=RETURN, pelo caminho de LPN que já existe.
 *
 * INDEPENDENTE do módulo `rmas` (pós-venda ML) — a integração é fase futura;
 * o campo `reference` é o elo manual até lá.
 */
const WarehouseReturnsPage = () => {
    const { label } = useWarehouseLabels();
    const toast = useToast();
    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [isTriageOpen, setTriageOpen] = useState(false);
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [statusSaving, setStatusSaving] = useState(false);

    const debouncedSearch = useDebounce(searchInput, 300);

    const loadReturns = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const params = { limit: pageSize, offset };
            if (statusFilter) {
                params.status = statusFilter;
            }
            const search = debouncedSearch.trim();
            if (search) {
                params.search = search;
            }
            const data = await api.getWarehouseReturns(params);
            const list = Array.isArray(data?.results) ? data.results : [];
            setRows(list);
            setPaging(ExtractPaging(data, { total: list.length, limit: pageSize, offset }));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [page, pageSize, statusFilter, debouncedSearch]);

    useEffect(() => {
        loadReturns();
    }, [loadReturns]);

    const loadDetail = useCallback(async () => {
        if (!selectedId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        setDetailError(null);
        try {
            const data = await api.getWarehouseReturn(selectedId);
            setDetail(data || null);
        } catch (error) {
            setDetailError(error?.message || 'Erro ao carregar');
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    }, [selectedId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const refreshAll = useCallback(() => {
        loadReturns();
        loadDetail();
    }, [loadReturns, loadDetail]);

    const handleCreate = async (event) => {
        event.preventDefault();
        setCreating(true);
        setCreateError(null);
        try {
            const created = await api.createWarehouseReturn({
                ...(reference.trim() ? { reference: reference.trim() } : {}),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
            });
            toast.success(`${label('wms.returns.new_title')}: ${created?.code || ''}`);
            setCreateOpen(false);
            setReference('');
            setNotes('');
            setSelectedId(created?.id || null);
            loadReturns();
        } catch (error) {
            setCreateError(error?.message || 'Erro ao registrar');
        } finally {
            setCreating(false);
        }
    };

    const handleStatusToggle = async () => {
        if (!detail?.return) {
            return;
        }
        const next = detail.return.status === 'OPEN' ? 'DONE' : 'OPEN';
        setStatusSaving(true);
        try {
            await api.updateWarehouseReturn(detail.return.id, { status: next });
            refreshAll();
        } catch (error) {
            toast.error(error);
        } finally {
            setStatusSaving(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.returns.col.code'),
                sortable: false,
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'reference',
                label: label('wms.returns.col.reference'),
                sortable: false,
                getValue: (row) => row.reference || '',
                render: (row) => (row.reference ? <Mono>{row.reference}</Mono> : BLANK_CELL),
            },
            {
                key: 'items_count',
                label: label('wms.returns.col.items'),
                sortable: false,
                align: 'right',
                getValue: (row) => row.items_count ?? 0,
                render: (row) => <Mono>{FormatInt(row.items_count ?? 0)}</Mono>,
            },
            {
                key: 'status',
                label: label('wms.returns.col.status'),
                sortable: false,
                getValue: (row) => label(`wms.returns.status.${row.status}`),
                render: (row) => (
                    <Badge variant={row.status === 'DONE' ? 'success' : 'warning'}>
                        {label(`wms.returns.status.${row.status}`)}
                    </Badge>
                ),
            },
            {
                key: 'created_at',
                label: label('wms.lpn.field.created_at'),
                sortable: false,
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
        ],
        [label],
    );

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={5} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.returns.title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={loadReturns}
        />
    ) : (
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || index}
            onRowClick={(row) => setSelectedId(row.id)}
            countLabel={label('wms.returns.title')}
            exportFileName="wms-devolucoes"
            emptyMessage={label('wms.returns.empty')}
            searchPlaceholder={label('wms.returns.col.reference')}
            searchMode="server"
            searchTerm={searchInput}
            onSearchTermChange={setSearchInput}
            sortMode="server"
            paginationMode="server"
            totalItems={paging.total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    );

    const detailPanel = !selectedId ? (
        <div className="rounded-xl border border-dashed border-mono-300 p-8 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
            {label('wms.returns.select_hint')}
        </div>
    ) : detailLoading && !detail ? (
        <SkeletonTable columns={3} rows={5} />
    ) : detailError ? (
        <ErrorCard title={label('wms.returns.title')} message={detailError} action={label('wms.common.retry')} actionFn={loadDetail} />
    ) : detail ? (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <div>
                    <div className="flex items-center gap-2">
                        <Id value={detail.return.code} className="text-lg font-semibold" />
                        <Badge variant={detail.return.status === 'DONE' ? 'success' : 'warning'}>
                            {label(`wms.returns.status.${detail.return.status}`)}
                        </Badge>
                    </div>
                    {detail.return.reference ? (
                        <p className="mt-1 text-sm text-mono-600 dark:text-mono-400">
                            {label('wms.returns.col.reference')}: <Mono>{detail.return.reference}</Mono>
                        </p>
                    ) : null}
                    {detail.return.notes ? (
                        <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">{detail.return.notes}</p>
                    ) : null}
                </div>
                <div className="flex flex-col items-end gap-2">
                    <div className="flex gap-2">
                        <Button onClick={() => setTriageOpen(true)} disabled={detail.return.status !== 'OPEN'}>
                            <PlusCircle size={18} className="mr-2" />
                            {label('wms.returns.triage_action')}
                        </Button>
                        <Button variant="secondary" onClick={handleStatusToggle} loading={statusSaving}>
                            {detail.return.status === 'OPEN' ? (
                                <>
                                    <CheckCircle size={18} className="mr-2" />
                                    {label('wms.returns.mark_done')}
                                </>
                            ) : (
                                <>
                                    <ArrowClockwise size={18} className="mr-2" />
                                    {label('wms.returns.reopen')}
                                </>
                            )}
                        </Button>
                    </div>
                    {detail.return.status !== 'OPEN' ? (
                        <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.returns.done_hint')}</p>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-mono-200 bg-white p-4 text-center dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.returns.summary.sellable')}
                    </p>
                    <Mono className="text-2xl font-semibold text-green-600 dark:text-green-400">
                        {FormatInt(detail.summary.sellable_qty)}
                    </Mono>
                </div>
                <div className="rounded-xl border border-mono-200 bg-white p-4 text-center dark:border-mono-800 dark:bg-mono-950">
                    <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        {label('wms.returns.summary.supplier_rma')}
                    </p>
                    <Mono className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
                        {FormatInt(detail.summary.supplier_rma_qty)}
                    </Mono>
                </div>
            </div>

            <section className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                    {label('wms.returns.items_title')}
                </h3>
                {detail.items.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.returns.items_empty')}</p>
                ) : (
                    <ul className="space-y-2">
                        {detail.items.map((item) => (
                            <li
                                key={item.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-mono-100 px-3 py-2 text-sm dark:border-mono-900"
                            >
                                <div className="flex min-w-0 items-center gap-2">
                                    <Mono>{item.product_sku || item.product_id}</Mono>
                                    <span className="text-mono-500 dark:text-mono-400">×</span>
                                    <Mono className="font-semibold">{FormatInt(Number(item.qty))}</Mono>
                                    <Badge variant={item.disposition === 'SELLABLE' ? 'success' : 'warning'}>
                                        {label(`wms.returns.disposition.${item.disposition}`)}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-mono-500 dark:text-mono-400">
                                    <Mono>{item.lpn_code}</Mono>
                                    {item.lpn_location_code ? <Badge variant="neutral">{item.lpn_location_code}</Badge> : null}
                                    {item.employee_name ? (
                                        <span>
                                            {item.employee_name} (<Mono>{item.employee_code}</Mono>)
                                        </span>
                                    ) : null}
                                    <DateTimeText value={item.created_at} relative />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    ) : null;

    return (
        <div className="space-y-4">
            <PageHeader
                title={label('wms.returns.title')}
                subtitle={label('wms.returns.subtitle')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        <PlusCircle size={18} className="mr-2" />
                        {label('wms.returns.new_action')}
                    </Button>
                }
            />

            <div className="flex items-center gap-3">
                <Select
                    value={statusFilter}
                    onChange={(event) => {
                        setStatusFilter(event.target.value);
                        setPage(1);
                    }}
                    className="max-w-xs"
                >
                    <option value="">{label('wms.lpns.status_all')}</option>
                    <option value="OPEN">{label('wms.returns.status.OPEN')}</option>
                    <option value="DONE">{label('wms.returns.status.DONE')}</option>
                </Select>
            </div>

            <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start">
                <div className="min-w-0 flex-1">{listContent}</div>
                <aside className="w-full shrink-0 2xl:w-[640px]">{detailPanel}</aside>
            </div>

            <Modal
                isOpen={isCreateOpen}
                onClose={() => (creating ? null : setCreateOpen(false))}
                title={label('wms.returns.new_title')}
                size="md"
            >
                <form onSubmit={handleCreate} className="space-y-4">
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.returns.new_hint')}</p>
                    <Input
                        label={label('wms.returns.field.reference')}
                        value={reference}
                        onChange={(event) => setReference(event.target.value)}
                        disabled={creating}
                    />
                    <Textarea
                        label={label('wms.returns.field.notes')}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        rows={3}
                        disabled={creating}
                    />
                    {createError ? <p className="text-sm text-red-600 dark:text-red-400">{createError}</p> : null}
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setCreateOpen(false)}
                            disabled={creating}
                        >
                            {label('wms.common.cancel')}
                        </Button>
                        <Button type="submit" loading={creating}>
                            {label('wms.returns.new_action')}
                        </Button>
                    </div>
                </form>
            </Modal>

            {detail?.return ? (
                <ReturnTriageModal
                    isOpen={isTriageOpen}
                    onClose={() => setTriageOpen(false)}
                    returnEntry={detail.return}
                    label={label}
                    onCreated={refreshAll}
                />
            ) : null}
        </div>
    );
};

export default WarehouseReturnsPage;
